import { useState } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { useResources, useCreateResource, useDeleteResource, RESOURCE_CATEGORIES, RESOURCE_FORMATS } from "@/hooks/useResources";
import { useUserRoles } from "@/hooks/useUserRoles";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, Plus, Trash2, BookOpen, Search } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

export default function Resources() {
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newResource, setNewResource] = useState({
    title: "",
    category: "",
    format: "",
    link: "",
  });

  const { data: resources, isLoading } = useResources();
  const { data: roles } = useUserRoles();
  const createResource = useCreateResource();
  const deleteResource = useDeleteResource();

  const isAdmin = roles?.includes("admin") ?? false;
  const isInstructor = roles?.includes("instructor") ?? false;
  const canManageResources = isAdmin || isInstructor;

  const filteredResources = resources?.filter((resource) => {
    const matchesCategory = selectedCategory === "all" || resource.category === selectedCategory;
    const matchesSearch = resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      resource.category.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const groupedResources = filteredResources?.reduce((acc, resource) => {
    if (!acc[resource.category]) {
      acc[resource.category] = [];
    }
    acc[resource.category].push(resource);
    return acc;
  }, {} as Record<string, typeof filteredResources>);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newResource.title || !newResource.category || !newResource.format || !newResource.link) {
      return;
    }
    
    await createResource.mutateAsync(newResource);
    setNewResource({ title: "", category: "", format: "", link: "" });
    setIsDialogOpen(false);
  };

  const getFormatColor = (format: string) => {
    const colors: Record<string, string> = {
      Blog: "bg-blue-500/10 text-blue-600 border-blue-500/20",
      YouTube: "bg-red-500/10 text-red-600 border-red-500/20",
      LinkedIn: "bg-sky-500/10 text-sky-600 border-sky-500/20",
      Podcast: "bg-purple-500/10 text-purple-600 border-purple-500/20",
      Vimeo: "bg-cyan-500/10 text-cyan-600 border-cyan-500/20",
      Notion: "bg-gray-500/10 text-gray-600 border-gray-500/20",
      Newsletter: "bg-green-500/10 text-green-600 border-green-500/20",
      Classroom: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20",
      WhatsApp: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
      Resource: "bg-orange-500/10 text-orange-600 border-orange-500/20",
      Book: "bg-amber-500/10 text-amber-600 border-amber-500/20",
    };
    return colors[format] || "bg-muted text-muted-foreground";
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <BookOpen className="h-6 w-6 text-primary" />
              Resource Library
            </h1>
            <p className="text-muted-foreground mt-1">
              Curated resources to help you grow as an entrepreneur
            </p>
          </div>

          {canManageResources && (
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Resource
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New Resource</DialogTitle>
                  <DialogDescription>
                    Add a new resource to the library for entrepreneurs.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Title</Label>
                    <Input
                      id="title"
                      value={newResource.title}
                      onChange={(e) => setNewResource({ ...newResource, title: e.target.value })}
                      placeholder="Resource title"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="category">Category</Label>
                    <Select
                      value={newResource.category}
                      onValueChange={(value) => setNewResource({ ...newResource, category: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {RESOURCE_CATEGORIES.map((cat) => (
                          <SelectItem key={cat} value={cat}>
                            {cat}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="format">Format</Label>
                    <Select
                      value={newResource.format}
                      onValueChange={(value) => setNewResource({ ...newResource, format: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select format" />
                      </SelectTrigger>
                      <SelectContent>
                        {RESOURCE_FORMATS.map((fmt) => (
                          <SelectItem key={fmt} value={fmt}>
                            {fmt}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="link">Link</Label>
                    <Input
                      id="link"
                      type="url"
                      value={newResource.link}
                      onChange={(e) => setNewResource({ ...newResource, link: e.target.value })}
                      placeholder="https://..."
                      required
                    />
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={createResource.isPending}>
                      {createResource.isPending ? "Adding..." : "Add Resource"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search resources..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-full sm:w-[200px]">
              <SelectValue placeholder="Filter by category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {RESOURCE_CATEGORIES.map((cat) => (
                <SelectItem key={cat} value={cat}>
                  {cat}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Resource List */}
        {isLoading ? (
          <div className="space-y-6">
            {[1, 2, 3].map((i) => (
              <Card key={i}>
                <CardHeader>
                  <Skeleton className="h-6 w-32" />
                </CardHeader>
                <CardContent className="space-y-3">
                  {[1, 2, 3].map((j) => (
                    <Skeleton key={j} className="h-16 w-full" />
                  ))}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : groupedResources && Object.keys(groupedResources).length > 0 ? (
          <div className="space-y-6">
            {Object.entries(groupedResources)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([category, categoryResources]) => (
                <Card key={category}>
                  <CardHeader>
                    <CardTitle className="text-lg">{category}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="divide-y">
                      {categoryResources?.map((resource) => (
                        <div
                          key={resource.id}
                          className="flex items-center justify-between py-3 first:pt-0 last:pb-0 gap-4"
                        >
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{resource.title}</p>
                            <Badge
                              variant="outline"
                              className={`mt-1 ${getFormatColor(resource.format)}`}
                            >
                              {resource.format}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              asChild
                            >
                              <a
                                href={resource.link}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <ExternalLink className="h-4 w-4 mr-1" />
                                View
                              </a>
                            </Button>
                            {canManageResources && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteResource.mutate(resource.id)}
                                className="text-destructive hover:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <BookOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {searchQuery || selectedCategory !== "all"
                  ? "No resources found matching your criteria."
                  : "No resources available yet."}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
